/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Heading, NeedToKnow as NeedToKnowSarsaparilla, Stack } from 'sarsaparilla';

export default function NeedToKnow({ errors, isConsentChecked, onChangeConsentHandler }) {
    const isInvalid = !!errors?.General?.length;
    return (
        <div className="ia-need-to-know">
            <NeedToKnowSarsaparilla
                introText={
                    'Please carefully review the important information below and click the check box to agree to the rules and regulations. The information in this section only pertains to the Interagency Annual Pass. Other important visitation policies and details for individual federal recreation sites can be found on the Recreation.gov page for that location.'
                }
                checkboxId="consent"
                isChecked={isConsentChecked}
                isInvalid={isInvalid}
                onChange={() => {
                    onChangeConsentHandler(!isConsentChecked);
                }}
            >
                <Stack>
                    <Heading headingLevel={3} appearance="h6">
                        Pass Information
                    </Heading>
                    <p>
                        You are purchasing an Interagency Annual Pass. This is not an
                        alternative pass option such as the Senior Pass, Access Pass,
                        Military Pass, Volunteer Pass, or 4th Grade Pass. Only proceed
                        with this order if your intent is to purchase the Interagency
                        Annual Pass.
                    </p>
                    <p>
                        If you will be traveling in fewer than 3 weeks, you should
                        consider purchasing a pass at the first federal recreation site
                        you visit that issues passes rather than purchasing your pass
                        online as the pass may not arrive before your trip. A complete
                        list of participating locations that issue the Interagency Annual
                        Pass can be found at{' '}
                        <a href="https://www.nps.gov/planyourvisit/pickup-pass-locations.htm">
                            Places to Get Interagency Passes (U.S. National Park Service).
                        </a>
                    </p>
                    <p>
                        Interagency Annual Passes are valid for entrance or standard
                        amenity (day use) fees at participating federal recreation
                        locations. An Interagency Annual Pass admits the pass owner and
                        accompanying passengers in a private, non-commercial vehicle at
                        areas with vehicle fees, or the pass owner and up to 3 adults at
                        areas with per-person fees. Children 15 and younger are always
                        admitted for free.
                    </p>
                    <p>
                        The signed, physical Interagency Annual Pass must be presented
                        along with photo identification for validation when you arrive.
                        Interagency Annual Passes are not valid without pass owner
                        signature. Copies of the Interagency Annual Pass in paper,
                        digital, or other formats will not be accepted.
                    </p>
                    <p>
                        Interagency Annual Passes purchased as gifts have two blank
                        signature lines for the gift recipient(s) to sign.
                    </p>
                    <p>
                        Interagency Annual Passes are valid for a full year from the date
                        of purchase through the last day of that month in the following
                        year. For example, a pass purchased on May 5, 2023 is valid
                        through May 31, 2024.
                    </p>
                    <p>
                        Interagency Annual Passes can only be refunded if the pass has not
                        been signed and has not been used. If the pass arrives damaged, do
                        not sign it or use it. Return the pass by writing “Return to
                        Sender” on the original shipping envelope. Allow up to 4-6 weeks
                        for a refund.
                    </p>
                    <p>
                        Interagency Annual Passes are non-transferable, cannot be replaced
                        if lost or stolen, and are void if altered or reproduced.
                    </p>
                    <Heading headingLevel={3} appearance="h6">
                        Modifications & Cancellations
                    </Heading>
                    <p>
                        Interagency Annual Pass purchases can only be modified or
                        cancelled before 7pm (EST) on the date of purchase by calling
                        Recreation.gov at 877-444-6777. After this time, the order will
                        have shipped and cannot be cancelled. To make modifications or
                        cancel the Interagency Annual Pass after the 7pm (EST) deadline,
                        you must return the pass directly to USGS by writing “Return to
                        Sender” on the original shipping envelope. The pass cannot be
                        signed or used in order to be returned.
                    </p>
                    <p>
                        Shipping and handling fees for Interagency Passes are
                        non-refundable.
                    </p>
                </Stack>
            </NeedToKnowSarsaparilla>
        </div>
    );
}

NeedToKnow.propTypes = {
    errors: PropTypes.object.isRequired,
    isConsentChecked: PropTypes.bool.isRequired,
    onChangeConsentHandler: PropTypes.func.isRequired,
};
